import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, LineChart, IndianRupee, TrendingUp, Users, Zap } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { filterByRange } from './ReportFilters';


const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });


export default function DigitalMarketingROIReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [platform, setPlatform] = useState('all');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/digital-marketing-campaigns/');
        setCampaigns(resp?.results || resp?.data?.results || []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const filteredCampaigns = useMemo(() => {
    let filtered = filterByRange(campaigns, preset, customFrom, customTo, 'start_date');
    if (platform !== 'all') filtered = filtered.filter(c => c.platform === platform);
    return filtered;
  }, [campaigns, preset, customFrom, customTo, platform]);

  const platformOptions = useMemo(() => {
    const seen = new Set();
    const opts = [{ value: 'all', label: 'All platforms' }];
    campaigns.forEach(c => {
      if (c.platform && !seen.has(c.platform)) {
        seen.add(c.platform);
        opts.push({ value: c.platform, label: c.platform_display || c.platform });
      }
    });
    return opts;
  }, [campaigns]);

  const totals = useMemo(() => filteredCampaigns.reduce((acc, c) => ({
    spend: acc.spend + Number(c.spend || 0),
    budget: acc.budget + Number(c.budget || 0),
    leads: acc.leads + Number(c.linked_enquiries_count || 0),
    converted: acc.converted + Number(c.converted_count || 0),
  }), { spend: 0, budget: 0, leads: 0, converted: 0 }), [filteredCampaigns]);

  const overallCpl = totals.leads ? totals.spend / totals.leads : 0;
  const overallCpa = totals.converted ? totals.spend / totals.converted : 0;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-sm">
          <LineChart className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Digital Marketing ROI</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Per-campaign CPL, CPA, ROAS</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[{ label: 'Platform', value: platform, onChange: setPlatform, options: platformOptions }]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-amber-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Total spend</span></div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">₹ {formatINR(totals.spend)}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Total leads</span></div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{totals.leads}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Zap className="w-4 h-4 text-purple-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Avg CPL</span></div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">₹ {formatINR(overallCpl)}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Avg CPA</span></div><div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">₹ {formatINR(overallCpa)}</div></div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Campaign</th>
              <th className="px-4 py-2 text-left font-medium">Platform</th>
              <th className="px-4 py-2 text-right font-medium">Spend</th>
              <th className="px-4 py-2 text-right font-medium">Leads</th>
              <th className="px-4 py-2 text-right font-medium">Converted</th>
              <th className="px-4 py-2 text-right font-medium">CPL</th>
              <th className="px-4 py-2 text-right font-medium">CPA</th>
              <th className="px-4 py-2 text-right font-medium">Conv. %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            : filteredCampaigns.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No campaigns match filters.</td></tr>
            : filteredCampaigns.map(c => {
              const cpl = c.linked_enquiries_count > 0 ? Number(c.spend) / c.linked_enquiries_count : 0;
              const cpa = c.converted_count > 0 ? Number(c.spend) / c.converted_count : 0;
              const convPct = c.linked_enquiries_count > 0 ? Math.round(c.converted_count / c.linked_enquiries_count * 100) : 0;
              return (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5"><Link to={`/engage/digital-marketing/${c.id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{c.name}</Link></td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{c.platform_display}</td>
                  <td className="px-4 py-2.5 text-right text-gray-900 dark:text-white">₹ {formatINR(c.spend)}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{c.linked_enquiries_count || 0}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400 font-medium">{c.converted_count || 0}</td>
                  <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">₹ {formatINR(cpl)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-amber-600 dark:text-amber-400">{cpa > 0 ? `₹ ${formatINR(cpa)}` : '—'}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                      convPct >= 20 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      convPct >= 10 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300')}>{convPct}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
